import type { StateCreator } from 'zustand';
import type { StoreState } from '../useStore';
import type { GrammarTopicProgress, QuizAnswerResult } from '@/types/quiz';
import * as dataApi from '../../services/dataApi';
import { reportError } from '@/services/errorTracking';

export interface GrammarProgressSlice {
  grammarProgress: GrammarTopicProgress[];
  setGrammarProgress: (progress: GrammarTopicProgress[]) => void;
  recordQuizResult: (
    guideId: string,
    sectionIndex: number,
    results: QuizAnswerResult[],
  ) => Promise<void>;
  getGuideAccuracy: (guideId: string) => number | null;
  getSectionAccuracy: (guideId: string, sectionIndex: number) => number | null;
}

export const createGrammarProgressSlice: StateCreator<StoreState, [], [], GrammarProgressSlice> = (
  set,
  get,
) => ({
  grammarProgress: [],

  setGrammarProgress: (grammarProgress) => set({ grammarProgress }),

  recordQuizResult: async (guideId, sectionIndex, results) => {
    const correctCount = results.filter((r) => r.isCorrect).length;
    const totalCount = results.length;

    // Optimistic update
    const prev = get().grammarProgress;
    const existing = prev.find((p) => p.guideId === guideId && p.sectionIndex === sectionIndex);

    const updated: GrammarTopicProgress = existing
      ? {
          ...existing,
          totalAttempts: existing.totalAttempts + totalCount,
          correctAttempts: existing.correctAttempts + correctCount,
          lastAttemptedAt: new Date().toISOString(),
        }
      : {
          guideId,
          sectionIndex,
          totalAttempts: totalCount,
          correctAttempts: correctCount,
          lastAttemptedAt: new Date().toISOString(),
        };

    set({
      grammarProgress: existing
        ? prev.map((p) => (p.guideId === guideId && p.sectionIndex === sectionIndex ? updated : p))
        : [...prev, updated],
    });

    // Persist to Supabase
    try {
      await dataApi.upsertGrammarProgress(guideId, sectionIndex, correctCount, totalCount);
    } catch (err) {
      // Rollback
      set({ grammarProgress: prev });
      reportError(err, { operation: 'recordQuizResult' });
    }
  },

  getGuideAccuracy: (guideId) => {
    const entries = get().grammarProgress.filter((p) => p.guideId === guideId);
    if (entries.length === 0) return null;
    const total = entries.reduce((sum, e) => sum + e.totalAttempts, 0);
    const correct = entries.reduce((sum, e) => sum + e.correctAttempts, 0);
    return total > 0 ? correct / total : null;
  },

  getSectionAccuracy: (guideId, sectionIndex) => {
    const entry = get().grammarProgress.find(
      (p) => p.guideId === guideId && p.sectionIndex === sectionIndex,
    );
    if (!entry || entry.totalAttempts === 0) return null;
    return entry.correctAttempts / entry.totalAttempts;
  },
});
